import { useRef } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Textfield
} from '@clientbase/clientbase-library'
import { yupResolver } from '@hookform/resolvers/yup'
import { Stack, Typography, useMediaQuery } from '@mui/material'
import { DataService } from 'api/DataService'
import * as yup from 'yup'

import IconContainer from 'components/IconContainer/IconContainer'

import { toastProps } from 'utils/types/toast-props'

import {
  ColorPallete,
  GroupWrapper,
  InputColor,
  InputColorWrapper,
  Label,
  LabelText
} from './styles'

const schema = yup.object({
  name: yup.string().required('Nome do grupo é obrigatório.'),
  groupColor: yup
    .string()
    .matches(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/gm, 'Campo inválido.')
    .required()
})

type CreateGroupSchema = yup.InferType<typeof schema>

type CreateGroupModalProps = {
  open: boolean
  onClose: () => void
  onGroupCreated: (group: { label: string; value: string }) => void
}

const CreateGroupModal = ({
  open,
  onClose,
  onGroupCreated
}: CreateGroupModalProps) => {
  const isMobile = useMediaQuery('(max-width: 960px)')
  const formRef = useRef<HTMLFormElement | null>(null)

  const { handleSubmit, control, reset } = useForm<CreateGroupSchema>({
    defaultValues: {
      name: '',
      groupColor: '#d8d8d8'
    },
    resolver: yupResolver(schema)
  })

  const createGroup: SubmitHandler<CreateGroupSchema> = async (data) => {
    const id = toast.loading('Criando grupo...')

    const response = await DataService({
      url: '/v1/customer_groups',
      type: 'POST',
      data: {
        name: data.name,
        group_color: data.groupColor
      }
    })

    const errorMessage =
      response.error && (response.detail_error || response.message)

    toast.update(id, {
      render: response.error ? errorMessage : response.data.message,
      type: response.error ? 'error' : 'success',
      ...toastProps
    })

    if (!response.error) {
      const newGroup = {
        label: data.name,
        value: response.data.record?.uuid || response.data.uuid
      }
      reset()
      onClose()
      onGroupCreated(newGroup)
    }
  }

  const handleSubmitButton = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()
    await handleSubmit(createGroup)()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      handleSubmit(createGroup)()
    }
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  return (
    <Modal
      handleClose={handleClose}
      open={open}
      {...(!isMobile ? { height: 'auto !important' } : {})}
    >
      <ModalBody>
        <ModalHeader onClose={handleClose}>
          <Stack direction="row" alignItems="center" gap={2}>
            <IconContainer icon="add" />
            <Typography variant="mdBold">Novo grupo</Typography>
          </Stack>
        </ModalHeader>

        <ModalContent>
          <form
            ref={formRef}
            onKeyDown={handleKeyDown}
            style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
          >
            <Controller
              name="name"
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error }
              }) => (
                <Textfield
                  onChange={onChange}
                  value={value}
                  helperText={error?.message}
                  error={!!error?.message}
                  label="Nome do grupo:"
                  inputProps={{ maxLength: 255 }}
                />
              )}
            />

            <GroupWrapper>
              <Controller
                name="groupColor"
                control={control}
                render={({ field }) => (
                  <Label>
                    <LabelText>Cor do ícone do grupo:</LabelText>
                    <InputColorWrapper>
                      <ColorPallete color={field?.value || '#d8d8d8'} />
                      <Typography>{field?.value}</Typography>
                      <InputColor type="color" id="color" {...field} />
                    </InputColorWrapper>
                  </Label>
                )}
              />
            </GroupWrapper>
          </form>
        </ModalContent>

        <ModalFooter>
          <Button fullWidth variantButton="neutral" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            fullWidth
            variantButton="primary"
            onClick={handleSubmitButton}
          >
            Salvar
          </Button>
        </ModalFooter>
      </ModalBody>
    </Modal>
  )
}

export default CreateGroupModal
